/**
 * Authorization
 *
 * @module auth
 */

import {appRouter} from '@netx/core-next/instance/router';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {getUserLevel} from '@netx/core-user-next/utils/getUserLevel';

// Models
import UserProfileModel from '@netx/core-user/models/userProfile';

// Collections
import CustomFormCollection from '@netx/core-app/collections/customForm';

import {publicProperties} from '@netx/core-app/collections/singleton/publicProperties';
import {getPublicProperty} from '@netx/core-app-next/utils/getPublicProperty';

// Views
import AuthUserLoginView from './views/authUserLoginView';
import Netx from '@netx/core/bootstrap';
import coreConfig from '@netx/core-next/config';
import {session} from '@netx/core-auth/collections/singleton/session';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import {appViews} from '@netx/core-app/singletons/appViews';
import {whenReady} from '@netx/core-next/whenReady';

// App config `Netx.auth.options`
import customConfig from './config';

import routes from './routes';

import {getPrefixedLogger, localStore} from '@netx/core-next/instance';
import {setGlobal} from '@netx/core-next/utils/setGlobal';
import {
	canRequestPasswordReset,
	canSelfRegister,
} from '@netx/core-auth-next/utils';

const logger = getPrefixedLogger('core:auth');

const IGNORE_SESSION_ROUTES = [
	'password-reset',
	'passwordReset',
	'password',
	'register',
];

const IGNORE_POST_LOGIN_ROUTES = IGNORE_SESSION_ROUTES.concat(['login']);

/**
 * @event module:auth#auth:init
 */
dispatcher.once('auth:init', function (options) {
	options = _.extend({}, options, customConfig);

	const NS = (Netx.auth = {
		bootstrap: [],
		options: options,
	});

	setGlobal({session});

	// Helper functions
	Object.assign(Netx, {
		/**
		 * @function
		 * @param {string} key [dot seperated] property key
		 * @param {boolean} isTrue Test if the property value is true
		 * @return {boolean|mixed}
		 */
		getPublicProperty: function (key, isTrue) {
			return publicProperties[isTrue ? 'isTrue' : 'getProperty'](key);
		},
		/**
		 * Get the current session
		 * @function
		 * @param {string} attr Attribute to retrieve (if omitted the session model is returned)
		 * @return {Netx.Models.Session|mixed}
		 */
		getSession: function (attr) {
			return attr ? session.get(attr) : session;
		},
		/**
		 * Get the current token
		 * @function
		 * @param {string} attr Attribute to retrieve (if omitted the user model is returned)
		 * @return {Netx.Models.UserProfile|mixed}
		 */
		getToken: function () {
			return session.getToken();
		},
	});

	// Create netx session
	// This will test the browser and also the remote DAM connection
	// if DAM_URL is specified in config.js using the Netx.session.testAjax() method.
	// See netx-session.js for more info
	// When this completes, it triggers
	var nxSession = session; //(Netx.session = session);

	// Wait for the session to configure (testAjax getPublicProperties return)
	nxSession.configured.then(function (props) {
		// Set the public properties returned from session configure
		publicProperties.set(props);

		/**
		 * If a users browser is not officially supported give them the option to manually
		 * override
		 * @event module:app#app:browser:override:confirmed
		 */
		dispatcher.once('app:browser:override:confirmed', function () {
			// If the user wants to pull the trigger...
			nxSession.set('browserOk', true);
			nxSession.setCookie('browserOk', true, 30);
		});

		/**
		 * When app:compatible:confirmed is triggered, we have an API connection
		 * to a compatible DAM version, and a valid session.
		 * @event module:app#app:compatible:confirmed
		 */
		dispatcher.once('app:compatible:confirmed', function () {
			// We showed them this already?
			nxSession.deleteCookie('sessionExpired');
			// Never let our session expire:
			NS.options.TICKLE_SESSION && nxSession.beTickling();
		});

		/**
		 * All required modules are initialized and we can check the state of the session
		 * and dispatch accordingly
		 * @event module:app#app:initialized
		 * @fires module:auth#auth:token:valid
		 * @fires module:auth#auth:token:invalid
		 * @fires module:auth#auth:session:valid
		 * @fires module:auth#auth:session:invalid
		 */
		dispatcher.once('app:initialized', function () {
			// Are we doing token-based authentication instead of user login?
			if (nxSession.getToken()) {
				// We have a token. Test it.
				nxSession.isValid().then(
					function () {
						// We have a valid token.
						dispatcher.trigger('auth:token:valid');
					},
					function () {
						// Token is not valid; fail gracefully
						dispatcher.trigger('auth:token:invalid');
					},
				);
			} else {
				// Do we have a valid session (are we logged in already?)
				nxSession.isValid().then(
					function () {
						// success handler == found a session!
						// Fetch the user
						userProfile.fetch().then(function () {
							dispatcher.trigger('auth:session:valid');
						});
					},
					function (err) {
						// isValid() fail handler (no valid session == user is not logged in)
						dispatcher.trigger('auth:session:invalid');
					},
				);
			}
		});

		/**
		 * Triggering app:stop will halt the application, log out the user and
		 * reload the page. Triggered due to invalid session, expired session or logout.
		 * @event module:app#app:stop
		 */
		dispatcher.once('app:stop', function () {
			// Delete the sessionKey cookie
			nxSession.stopTickling();
			nxSession.deleteCookie('sessionKey');
		});

		/**
		 * If we don't have a valid session at startup, netx:session:invalid is triggered.
		 * Check for various special cases, or else show a login form.
		 * @event module:auth#auth:session:invalid
		 * @fires module:auth#auth:user:login
		 * @fires module:auth#auth:user:login:confirmed
		 */
		dispatcher.once('auth:session:invalid', function () {
			const m = window.location.hash.match(/(\w+)/i)?.[1] ?? '';

			// Nothing to process in regards to session validity
			if (IGNORE_SESSION_ROUTES.includes(m)) return;

			const {hash} = window.location;

			// Check if the user was redirected to a custom login
			const customLoginWasTriggered =
				localStore.get(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME) != null;

			// Do not store any routes that would lead a user to wind up here
			let postLoginHash = IGNORE_POST_LOGIN_ROUTES.includes(
				hash.replace(/^#/, ''),
			)
				? ''
				: hash;

			// If the user was redirected from a custom login, do not blow out the post login hash
			if (postLoginHash === '' && customLoginWasTriggered) {
				// use the already stored hash
				postLoginHash = localStore.get(customConfig.POST_LOGIN_HASH_NAME) || '';
			}

			// if login is required, show the form
			if (coreConfig.REQUIRE_LOGIN) {
				// Refactor ticket - DAM-10298
				// use localStorage to save hash the user came in with for successful login
				localStore.set(customConfig.POST_LOGIN_HASH_NAME, postLoginHash);
				// remove - if we hit the custom login logic it will be set again
				localStore.remove(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME);

				// DAM-11713 allow users to authenticate via NetX bypassing SSO and/or custom login
				if (_.contains(['', '#login'], hash)) {
					//incase #login already present
					appRouter
						.navigate('', {trigger: false})
						.navigate('login', {trigger: true});
					return;
				}

				// DAM-9976 support deep links via SSO
				// The system property `custom.index_page` was added for use with the original SSO implementation
				// Its use has been augemented to include custom home pages that do not utilize SSO
				// Redirection to the value of the property is handled exclusively by NetX's /index.jsp page
				if (
					!_.isUndefined(publicProperties.getProperty('custom.index_page')) &&
					publicProperties.getProperty('custom.index_page') !== ''
				) {
					// Set for 5 minutes - will get blown out on each trip through this event as well
					localStore.set(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME, 1);
					localStore.expire(
						customConfig.CUSTOM_LOGIN_TRIGGERED_NAME,
						60 * 1000 * 5,
					);
					window.location.replace(window.location.origin);
					return;
				}

				// DAM-10369 support for custom login page
				// The system property `login.custom` was added to allow clients to utilize a custom authentication page
				// It is a boolean property that when true redirects unauthenticated users to /login.jsp
				// Redirection to the /login.jsp page is handled exclusively by NetX's /index.jsp page
				if (publicProperties.isTrue('login.custom')) {
					// Set for 5 minutes - will get blown out on each trip through this event as well
					localStore.set(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME, 1);
					localStore.expire(
						customConfig.CUSTOM_LOGIN_TRIGGERED_NAME,
						60 * 1000 * 5,
					);
					window.location.replace(window.location.origin);
					return;
				}

				//incase #login already present
				appRouter
					.navigate('', {trigger: false})
					.navigate('login', {trigger: true});
				return;
			}

			// DEVNOTE: This seems like a questionable tactic? @spence
			// do a null auto-login if logins are not required.
			// THEM-1809 - public user (clean only) also wants deep linking
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, postLoginHash);
			// login as public user
			dispatcher.trigger('auth:user:login:confirmed');
		});

		/**
		 * Valid session
		 * @event modeule:auth#auth:session:valid
		 */
		dispatcher.once('auth:session:valid', function () {
			// the expected route will occur at app start
			const hash = window.location.hash;
			if (IGNORE_POST_LOGIN_ROUTES.includes(hash.replace(/^#/, ''))) {
				appRouter.navigate('', {trigger: false});
			}
		});

		/**
		 * Invalid token
		 * @event modeule:auth#auth:token:invalid
		 */
		dispatcher.once('auth:token:invalid', function () {
			// Only for main app
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, '');
		});

		/**
		 * Valid token
		 * @event modeule:auth#auth:token:valid
		 */
		dispatcher.once('auth:token:valid', function () {
			// Only for main app
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, '');
		});

		// User login
		/**
		 * @route
		 * @fires module:auth#auth:user:login
		 */
		appRouter.on('route:auth:user:login', function () {
			// if using public user, allow login screen so you don't get stuck with it
			if (
				!(getPublicProperty('viewer.userLogin') === userProfile.get('login')) &&
				nxSession &&
				getUserLevel() > 0
			) {
				appRouter.navigate('', {trigger: true});
			} else {
				dispatcher.trigger('auth:user:login', true);
			}
		});
		/**
		 * @event module:user#user:login:show
		 * @param {object} viewOptions View options
		 */
		dispatcher.on(
			'auth:user:login:show',
			function (viewOptions) {
				appViews.add({
					viewClass: AuthUserLoginView,
					showActions: ['auth:user:login:show'],
					viewOptions: viewOptions,
				});
			},
			window,
		);

		/**
		 * User login
		 * @event module:auth#auth:user:login
		 * @fires module:auth#auth:user:login:expired
		 * @fires module:auth#auth:user:login:show
		 */
		dispatcher.on('auth:user:login', function (isRouted) {
			if (nxSession.sessionExpired() && nxSession.get('loginMsg') === '') {
				dispatcher.trigger('auth:user:login:expired');
			}

			// Wait for app ready
			whenReady(function () {
				// Show login
				dispatcher.trigger('auth:user:login:show', {
					model: nxSession,
					collection: nxSession.get('samlServices'),
					isRouted: isRouted,
					routeMatch: new RegExp(/^login/),
				});
			}, NS);
		});

		/**
		 * @event module:auth#auth:user:login:confirmed
		 * @param {object} values Form data
		 * @fires module:auth#auth:user:login:error
		 * @fires module:auth#auth:user:login:invalid
		 * @fires module:auth#auth:user:login:success
		 * @fires module:auth#auth:user:login:lockout
		 */
		dispatcher.on('auth:user:login:confirmed', function (values) {
			// Does the NetX instance use the public user
			if (_.isUndefined(values) && !coreConfig.REQUIRE_LOGIN) {
				// Fake the login form object
				values = {
					password: null,
					username: null,
				};
			}

			// Login
			userProfile.userLogin(values).then(
				function (sessionKey) {
					if (sessionKey !== '-1') {
						nxSession.setCookie('sessionKey', sessionKey, 1);
						nxSession.setKey(sessionKey);
						dispatcher.trigger('auth:user:login:success');
						// Perform a hard refresh to trigger the browsers' password saving prompt HTML-9
						window.location.reload(true);
					} else {
						dispatcher.trigger('auth:user:login:invalid');
					}
				},
				function (err) {
					if (err.code === 3006) {
						dispatcher.trigger('auth:user:login:lockout');
					} else {
						dispatcher.trigger(
							'auth:user:login:error',
							new Error(i18n.t('i18n.loginErrorOccurred')),
						);
					}
				},
			);
		});

		/**
		 * @event module:auth#auth:user:login:expired
		 */
		dispatcher.on('auth:user:login:expired', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.youWereLoggedOut', {
					defaultValue: 'Logged out due to inactivity',
				}),
				loginMsgClass: 'has-error',
			});
		});
		/**
		 * @event module:auth#auth:user:login:lockout
		 */
		dispatcher.on('auth:user:login:lockout', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.loginLockedOutMessage'),
				loginMsgClass: 'has-error',
			});
		});

		/**
		 * @event module:auth#auth:user:login:invalid
		 */
		dispatcher.on('auth:user:login:invalid', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.loginIncorrect'),
				loginMsgClass: 'has-error',
			});
		});

		/**
		 * @event module:auth#auth:user:login:error
		 * @param {string} error Error string
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:login:error',
			function (error) {
				dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
			},
			window,
		);

		// User logout
		appRouter.on('route:auth:user:logout', function () {
			dispatcher.trigger('auth:user:logout');
		});
		/**
		 * @event module:auth#auth:user:logout:routed
		 * @fires module:app#app:stop
		 * @fires module:app#app:alert
		 */
		dispatcher.on('auth:user:logout', function () {
			userProfile.logout().then(
				function () {
					// DAM-9360 - old school logout page boolean property
					if (getPreference('user.logoutPage', true)) {
						window.location.href = '/logout.html';
					} else {
						// Router navigation needs to occur before 'app:stop' is triggered
						appRouter.navigate('');
						// Trigger application shutdown
						dispatcher.trigger('app:stop');
					}
				},
				function (err) {
					// Session ended before we logged out?
					dispatcher.trigger(
						'app:alert',
						i18n.t('i18n.sessionInvalid'),
						'Warning',
					);
					// Whatever the problem, log out anyway in 3 secs.
					_.delay(function () {
						appRouter.navigate('');
						dispatcher.trigger('app:stop');
					}, 3000);
				},
			);
		});

		/**
		 * SAML authentication
		 * @event module:auth#auth:user:saml:navigate
		 * @param {string} samlServiceName SAML service name
		 */
		dispatcher.on('auth:user:saml:navigate', function (samlServiceName) {
			// navigate browser to saml endpoint
			window.location.href = '/saml/' + samlServiceName;
		});

		// User registration
		appRouter.on('route:auth:user:register', function () {
			if ((nxSession && getUserLevel() > 0) || !canSelfRegister()) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:register');
		});
		/**
		 * @event module:auth#auth:user:register:show
		 * @param {object} viewOptions View options
		 */
		dispatcher.on(
			'auth:user:register:show',
			function (viewOptions) {
				import(
					/* webpackChunkName: "view-authUserRegister" */ './views/authUserRegisterView'
				).then((View) => {
					appViews.add({
						viewClass: View.default,
						showActions: ['auth:user:register:show'],
						viewOptions: viewOptions,
					});
				});
			},
			window,
		);
		/**
		 * Register routed
		 * creates/shows register form
		 * @event module:auth#auth:user:register
		 * @todo  DAM-8523
		 */
		dispatcher.on('auth:user:register', function () {
			// Let the app know we are ready and wait for it to respond
			whenReady(function () {
				var customForm = new CustomFormCollection([], {
					formName: 'register',
				});

				customForm.fetch().then(function () {
					// Show registration
					dispatcher.trigger('auth:user:register:show', {
						model: new UserProfileModel(),
						collection: customForm,
						eventStr: 'auth:user:register',
						requiresChanges: true,
						routeMatch: new RegExp(/^register/),
					});
				});
			}, NS);
		});
		/**
		 * @event module:auth#auth:user:register:confirmed
		 * @param {object} values Form values
		 * @param {Netx.Models.UserProfile} model User progile model
		 * @fires module:auth#auth:user:register:error
		 * @fires module:auth#auth:user:register:failure
		 * @fires module:auth#auth:user:register:success
		 * @fires module:auth#auth:user:register:awaiting:approval
		 */
		dispatcher.on('auth:user:register:confirmed', function (values, model) {
			// Get only the values pertinant to our model's attributes
			// var modelValues = _.pick.apply(
			// 	null,
			// 	[values].concat(_.keys(model.attributes)),
			// );

			// set custom user data if any
			var modelValues = model.setPropertyArrays(values);

			// Password has not changed
			if (
				!values.autogeneratePassword &&
				(modelValues.password1 === '********' ||
					modelValues.password2 === '********')
			) {
				modelValues = _.omit(modelValues, 'password1', 'password2');
			}
			// Autogenerate password (send empty strings to server)
			else if (values.autogeneratePassword) {
				modelValues.password1 = modelValues.password2 = '';
			}

			// ON-1732 - use the email as username when system property set to true
			if (getPublicProperty('user.force_email_login', true)) {
				modelValues.login = modelValues.email;
			}

			model.registerNewUser(modelValues).then(
				function (response) {
					switch (model.get('registration')) {
						case 'success':
							// Beware it is a string and not actually a boolean
							var autoLoginStr = getPublicProperty(
								'user.self_registration_auto_login',
							);
							if (autoLoginStr === 'true') {
								// Just log them in
								// get hash user navigated to app with, set without triggering, clean up
								appRouter.navigate(
									localStore.get(
										customConfig.POST_LOGIN_HASH_NAME ||
											coreConfig.HOME_ROUTE ||
											'',
									),
									{trigger: false},
								);
								localStore.set(customConfig.POST_LOGIN_HASH_NAME, '');
								nxSession.set({
									loginMsg: i18n.t('i18n.registrationSuccessful'),
									loginMsgClass: 'has-success',
								});
								// Perform a hard refresh to begin a new session with the newly registered user
								window.location.reload(true);
							} else {
								// Registration request was successful
								// Must wait for administraive approval
								appRouter.navigate('register/approval-needed', {trigger: true});
							}
							dispatcher.trigger('auth:user:register:success', model, true);
							break;
						case 'failure':
							dispatcher.trigger('auth:user:register:failure');
							break;
						case 'error':
							dispatcher.trigger('auth:user:register:error');
							break;
					}
				},
				function (err) {
					dispatcher.trigger('auth:user:register:error', err);
				},
			);
		});

		/**
		 * @event module:auth#auth:user:register:cancelled
		 */
		dispatcher.on(
			'auth:user:register:cancelled',
			function () {
				appRouter.navigate('login', {trigger: true});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:register:success
		 * @param {Netx.Models.UserProfile} model User progile model
		 * @param {boolean} awaitingApproval Awaiting approval
		 */
		dispatcher.on(
			'auth:user:register:success',
			function (model, awaitingApproval) {
				//
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:register:failure
		 * @fires module:auth#auth:user:register:error
		 */
		dispatcher.on(
			'auth:user:register:failure',
			function () {
				dispatcher.trigger('auth:user:register:error');
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:register:error
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:register:error',
			function () {
				dispatcher.trigger(
					'app:alert',
					i18n.t('i18n.registrationError'),
					i18n.t('i18n.error'),
				);
			},
			window,
		);

		// User registration awaitng approval
		appRouter.on('route:auth:user:register:approvalNeeded', function () {
			dispatcher.trigger('auth:user:register:awaiting:approval', true);
		});
		/**
		 * @event module:auth#auth:user:register:awaiting:approval:show
		 * @param {object} viewOptions View options
		 */
		dispatcher.on(
			'auth:user:register:awaiting:approval:show',
			function (viewOptions) {
				import(
					/* webpackChunkName: "view-authUserAwaitingApproval" */ './views/authUserAwaitingApprovalView'
				).then((View) => {
					appViews.add({
						viewClass: View.default,
						showActions: ['auth:user:register:awaiting:approval:show'],
						viewOptions: viewOptions,
					});
				});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:register:awaiting:approval
		 * @fires module:auth#auth:user:register:awaiting:approval:show
		 */
		dispatcher.on('auth:user:register:awaiting:approval', function (isRouted) {
			// Show
			dispatcher.trigger('auth:user:register:awaiting:approval:show', {
				isRouted: isRouted,
				routeMatch: new RegExp(/^register\/approval-needed/),
			});
		});

		// User password reset
		appRouter.on('route:auth:user:password:reset', function () {
			if (getUserLevel() > 0 || !canRequestPasswordReset()) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:password:request', true);
		});
		/**
		 * @event module:auth#auth:user:password:request:show
		 * @param {object} viewOptions View options
		 */
		dispatcher.on(
			'auth:user:password:request:show',
			function (viewOptions) {
				import(
					/* webpackChunkName: "view-authUserPassResetRequest" */ './views/authUserPassResetRequestView'
				).then((View) => {
					appViews.add({
						viewClass: View.default,
						showActions: ['auth:user:password:request:show'],
						viewOptions: viewOptions,
					});
				});
			},
			window,
		);
		/**
		 * Request reset password
		 * @event module:auth#auth:user:password:request
		 * @fires module:auth#auth:user:password:request:show
		 */
		dispatcher.on('auth:user:password:request', function (isRouted) {
			// Let the app know we are ready and wait for it to respond
			whenReady(function () {
				// Show password reset request
				dispatcher.trigger('auth:user:password:request:show', {
					model: userProfile,
					requiresChanges: true,
					isRouted: isRouted,
					routeMatch: new RegExp(/^password-request/),
				});
			}, NS);
		});
		/**
		 * Password reset request submitting
		 * @event module:auth#auth:user:password:request:confirmed
		 * @param {object} values From values
		 * @param {Netx.Models.UserProfile} model User progile model
		 * @fires module:auth#auth:user:password:request:error
		 * @fires module:auth#auth:user:password:request:success
		 */
		dispatcher.on(
			'auth:user:password:request:confirmed',
			function (values, model) {
				model.passwordRequest(values).then(
					function (response) {
						dispatcher.trigger('auth:user:password:request:success', model);
					},
					function (err) {
						dispatcher.trigger('auth:user:password:request:error', err, model);
					},
				);
			},
		);
		/**
		 * @event module:auth#auth:user:password:request:cancelled
		 */
		dispatcher.on(
			'auth:user:password:request:cancelled',
			function () {
				appRouter.navigate('login', {trigger: true});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:request:success
		 */
		dispatcher.on(
			'auth:user:password:request:success',
			function () {
				// override me
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:request:failure
		 */
		dispatcher.on(
			'auth:user:password:request:failure',
			function () {
				// override me
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:request:error
		 * @param {string} error Error string
		 * @param {Netx.Models.UserProfile} model User progile model
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:password:request:error',
			function (error, model) {
				appRouter.navigate('#login', {trigger: true});
				dispatcher.trigger(
					'app:alert',
					i18n.t('i18n.passwordResetRequestError'),
					i18n.t('i18n.error'),
				);
			},
			window,
		);

		appRouter.on('route:auth:user:password:change', function (login, token) {
			if (nxSession && getUserLevel() > 0) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:password:change', login, token, true);
		});
		/**
		 * @event module:auth#auth:user:password:change:show
		 * @param {object} viewOptions View options
		 */
		dispatcher.on(
			'auth:user:password:change:show',
			function (viewOptions) {
				import(
					/* webpackChunkName: "view-authUserPassReset" */ './views/authUserPassResetView'
				).then((View) => {
					appViews.add({
						viewClass: View.default,
						showActions: ['auth:user:password:change:show'],
						viewOptions: viewOptions,
					});
				});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:change
		 * @param {string} login Login
		 * @param {string} token Token
		 * @fires module:auth#auth:user:password:change:show
		 */
		dispatcher.on(
			'auth:user:password:change',
			function (login, token, isRouted) {
				// this is not for logged in users
				if (userProfile.id) {
					userProfile.logout().then(function () {
						window.location.reload(true);
					});
					return;
				}
				// this will find any strict password requirements
				userProfile.decodeUserToken({login: login, token: token});
				// Let the app know we are ready and wait for it to respond
				whenReady(function () {
					// Show password change
					dispatcher.trigger('auth:user:password:change:show', {
						model: userProfile,
						requiresChanges: true,
						isRouted: isRouted,
						routeMatch: new RegExp(/^password-reset\/(\d+)\/(\s+)/),
					});
				}, NS);
			},
		);
		/**
		 * @event module:auth#auth:user:password:change:confirmed
		 * @param {object} values From values
		 * @param {Netx.Models.UserProfile} model User progile model
		 * @fires module:auth#auth:user:password:change:error
		 * @fires module:auth#auth:user:password:change:success
		 */
		dispatcher.on(
			'auth:user:password:change:confirmed',
			function (values, model) {
				model.passwordChange(values).then(
					function (response) {
						if (response.resultBool) {
							dispatcher.trigger('auth:user:password:change:success', model);
						} else {
							// handled in authUserPassResetView
						}
					},
					function (err) {
						dispatcher.trigger('auth:user:password:change:error', err, model);
					},
				);
			},
		);
		/**
		 * @event module:auth#auth:user:password:change:cancelled
		 */
		dispatcher.on(
			'auth:user:password:change:cancelled',
			function () {
				appRouter.navigate('login', {trigger: true});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:change:success
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:password:change:success',
			function () {
				dispatcher.trigger('app:alert', i18n.t('i18n.passwordChangedLogIn'));
				appRouter.navigate('login', {trigger: true});
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:change:failure
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'user:password:change:failure',
			function () {
				dispatcher.trigger(
					'app:alert',
					i18n.t('i18n.passwordResetError'),
					i18n.t('i18n.error'),
				);
			},
			window,
		);
		/**
		 * @event module:auth#auth:user:password:change:error
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:password:change:error',
			function (errOb) {
				var errMsg = i18n.t('i18n.passwordResetError');
				if (errOb.message) {
					errMsg += ' ' + errOb.message;
				}
				dispatcher.trigger('app:alert', errMsg, i18n.t('i18n.error'));
			},
			window,
		);

		/**
		 * This route contains a session key; if we've come this far, it probably has already been parsed.
		 * We should just redirect to the main app.
		 */
		appRouter.on('route:auth:user:sessionKey', function (sessionKey) {
			if (session.getKey() != sessionKey) {
				// this should never happen:
				console.warn('session key mismatch in /session route!');
				//Netx.session.setKey(key); // can't hurt.  shouldn't be necessary.
			} else {
				appRouter.reroute('', true);
			}
		});

		// DAM-6494:
		// Get the list of public API calls.
		// (We need this before we make any private API calls.)
		//
		// For now, the list is globally available as Netx.session.get('publicMethods').
		logger.silly('bootstrapping...');
		nxSession.getPublicMethods().then(function () {
			// get saml services for login
			nxSession.getSamlServices().then(function () {
				// Wait for all boostrap loads to complete.
				$.when.apply($, NS.bootstrap).then(function () {
					delete NS.bootstrap;
					logger.silly('bootstrapped');
					dispatcher.trigger('auth:extend', NS);
					dispatcher.trigger('auth:initialized', NS);
				});
			});
		});
	});
});

export default {
	initialize(opts) {
		return new Promise((resolve, reject) => {
			logger.silly('initializing...');
			dispatcher.trigger('auth:init');
			dispatcher.once('auth:initialized', function () {
				logger.silly('initialized');
				resolve(routes);
			});
		});
	},
};
